import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";

function SavedFeeds() {
  const [savedFeeds, setSavedFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("pubDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const navigate = useNavigate();
  const { user } = useAuth();

  // Styles
  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "20px",
      backgroundColor: "#f8f9fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "15px 0",
      marginBottom: "20px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "600",
      color: "#2c3e50",
      margin: "0",
    },
    backButton: {
      backgroundColor: "#6c757d",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      fontSize: "14px",
      transition: "background-color 0.2s",
    },
    feedContainer: {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "20px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
    feedTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#2c3e50",
      marginTop: "0",
      marginBottom: "20px",
      paddingBottom: "10px",
      borderBottom: "1px solid #e9ecef",
    },
    list: {
      listStyle: "none",
      padding: "0",
      margin: "0",
    },
    listItem: {
      padding: "15px",
      borderBottom: "1px solid #e9ecef",
      position: "relative",
    },
    buttons: {
      display: "flex",
      gap: "10px",
      marginBottom: "10px",
    },
    deleteButton: {
      backgroundColor: "#e74c3c",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
      transition: "background-color 0.2s",
    },
    deleteButtonDisabled: {
      backgroundColor: "#bdc3c7",
      cursor: "not-allowed",
    },
    linkTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#2c3e50",
      textDecoration: "none",
      display: "block",
      marginBottom: "5px",
      transition: "color 0.2s",
    },
    date: {
      fontSize: "12px",
      color: "#6c757d",
    },
    sportBadge: {
      backgroundColor: "#e9ecef",
      color: "#495057",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "500",
      marginLeft: "10px",
    },
    clicksBadge: {
      backgroundColor: "#d1ecf1",
      color: "#0c5460",
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "500",
      marginLeft: "10px",
    },
    metaInfo: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "5px",
      marginTop: "5px",
    },
    errorMessage: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "12px 20px",
      borderRadius: "4px",
      marginBottom: "20px",
      border: "1px solid #f5c6cb",
      textAlign: "center",
    },
    loadingMessage: {
      textAlign: "center",
      padding: "40px 0",
      color: "#6c757d",
      fontSize: "18px",
    },
    noFeedsMessage: {
      textAlign: "center",
      padding: "40px 0",
      color: "#6c757d",
      fontSize: "16px",
    },
    sortContainer: {
      display: "flex",
      gap: "10px",
      marginBottom: "20px",
      alignItems: "center",
    },
    sortSelect: {
      padding: "6px 12px",
      fontSize: "14px",
      border: "1px solid #ced4da",
      borderRadius: "4px",
      backgroundColor: "white",
      color: "#495057",
    },
    sortLabel: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#495057",
    },
  };

  const fetchSavedFeeds = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/rss/api/saved-feeds`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSavedFeeds(data);
    } catch (error) {
      console.error("Error fetching saved feeds:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchSavedFeeds();
  }, [user]);

  // Sort feeds based on selected criteria
  const sortedFeeds = [...savedFeeds].sort((a, b) => {
    let aVal, bVal;

    switch (sortBy) {
      case "clicks":
        aVal = a.clicks || 0;
        bVal = b.clicks || 0;
        break;
      case "pubDate":
        aVal = new Date(a.pubDate);
        bVal = new Date(b.pubDate);
        break;
      case "title":
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        break;
      case "sport":
        aVal = (a.sport_name || "").toLowerCase();
        bVal = (b.sport_name || "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (sortOrder === "asc") {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this feed?")) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/rss/api/saved-feeds/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || `HTTP error! status: ${response.status}`
        );
      }

      if (data.success) {
        alert("Feed deleted successfully");
        setSavedFeeds((prevFeeds) =>
          prevFeeds.filter((feed) => feed.id !== id)
        );
      } else {
        throw new Error(data.message || "Failed to delete the feed");
      }
    } catch (error) {
      console.error("Error deleting feed:", error);
      alert(`Failed to delete feed: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div style={styles.container} className="rss-saved-feeds">
        <style>{`
          @media (max-width: 1024px) {
            .rss-saved-feeds { padding: 16px; }
          }
          @media (max-width: 768px) {
            .rss-saved-feeds { padding: 14px; }
            .rss-saved-header { flex-direction: column; align-items: flex-start; gap: 10px; }
            .rss-saved-header button { width: 100%; }
          }
          @media (max-width: 480px) {
            .rss-saved-feeds { padding: 12px; }
          }
        `}</style>
        <div style={styles.loadingMessage}>Loading saved feeds...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container} className="rss-saved-feeds">
        <style>{`
          @media (max-width: 1024px) {
            .rss-saved-feeds { padding: 16px; }
          }
          @media (max-width: 768px) {
            .rss-saved-feeds { padding: 14px; }
            .rss-saved-header { flex-direction: column; align-items: flex-start; gap: 10px; }
            .rss-saved-header button { width: 100%; }
          }
          @media (max-width: 480px) {
            .rss-saved-feeds { padding: 12px; }
          }
        `}</style>
        <div style={styles.errorMessage}>Error: {error}</div>
        <button onClick={fetchSavedFeeds} style={styles.backButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container} className="rss-saved-feeds">
      <style>{`
        @media (max-width: 1024px) {
          .rss-saved-feeds { padding: 16px; }
        }
        @media (max-width: 768px) {
          .rss-saved-feeds { padding: 14px; }
          .rss-saved-header { flex-direction: column; align-items: flex-start; gap: 10px; }
          .rss-saved-header button { width: 100%; }
          .rss-sort { flex-direction: column; align-items: flex-start; }
          .rss-sort select { width: 100%; }
          .rss-list-buttons { flex-direction: column; align-items: stretch; }
          .rss-list-buttons button { width: 100%; }
        }
        @media (max-width: 480px) {
          .rss-saved-feeds { padding: 12px; }
        }
      `}</style>
      <div style={styles.header} className="rss-saved-header">
        <h1 style={styles.title}>Saved RSS Feeds</h1>
        <button
          style={styles.backButton}
          onClick={() => navigate("/News-Home")}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#5a6268";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#6c757d";
          }}
        >
          Back to Dashboard
        </button>
      </div>

      <div style={styles.feedContainer}>
        <h2 style={styles.feedTitle}>Saved Articles</h2>

        {savedFeeds.length > 0 && (
          <div style={styles.sortContainer} className="rss-sort">
            <span style={styles.sortLabel}>Sort by:</span>
            <select
              style={styles.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="pubDate">Publication Date</option>
              <option value="clicks">Click Count</option>
              <option value="title">Title</option>
              <option value="sport">Sport Category</option>
            </select>
            <select
              style={styles.sortSelect}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        )}

        {savedFeeds.length > 0 ? (
          <ul style={styles.list}>
            {sortedFeeds.map((feed) => (
              <li key={feed.id} style={styles.listItem}>
                <div style={styles.buttons} className="rss-list-buttons">
                  <button
                    style={styles.deleteButton}
                    onClick={() => handleDelete(feed.id)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = "#c0392b";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = "#e74c3c";
                    }}
                  >
                    Delete
                  </button>
                </div>
                <a
                  href={feed.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.linkTitle}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = "#3498db";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = "#2c3e50";
                  }}
                >
                  {feed.title}
                </a>
                <div style={styles.metaInfo}>
                  <small style={styles.date}>
                    Published on: {new Date(feed.pubDate).toLocaleDateString()}
                  </small>
                  {feed.sport_name && (
                    <span style={styles.sportBadge}>🏆 {feed.sport_name}</span>
                  )}
                  <span style={styles.clicksBadge}>
                    👆 {feed.clicks || 0} clicks
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div style={styles.noFeedsMessage}>No saved feeds available.</div>
        )}
      </div>
    </div>
  );
}

export default SavedFeeds;
